import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Message } from "primereact/message";

import { ProspectStation } from "../../../../types/onboarding";
import useEmailPreview from "../../../../hooks/useEmailPreview";
import EmailPreview, { previewErrorMessage } from "./EmailPreview";
import { goLiveErrorMessage } from "./stationConfigErrors";

interface GoLiveDialogProps {
  visible: boolean;
  prospectId: string;
  stations: ProspectStation[];
  // Shown so the agent can see who is about to be emailed.
  customerEmail: string;
  onHide: () => void;
  // Resolves once the action has finished; the dialog closes either way, since
  // failures are reported on the panel behind it.
  onConfirm: (stationIds: string[]) => Promise<unknown>;
  // What the confirm button is doing, so the copy matches the stage.
  confirmLabel?: string;
  // eRound still has the location private, so publishing here records something
  // drivers cannot see. Advisory: the agent may proceed.
  privateInEround?: boolean;
}

/**
 * Confirms marking a prospect's stations as live (CWA-94).
 *
 * The Back Office publishes nothing: a location becomes visible to drivers when
 * an agent makes it public in eRound. This records that it happened and sends
 * the customer their confirmation email — so the copy asks the agent to confirm
 * the publication they have already made, rather than claiming to make it.
 *
 * Used for prospects with more than one station. The checkbox list decides
 * **which stations the customer's email names** and nothing else: the backend
 * derives every station's live status from the prospect, so the prospect goes
 * live as a whole whatever is ticked here. The copy says so, because an agent
 * reading this as a per-station switch would draw exactly the wrong conclusion.
 *
 * The email itself is previewed for the current selection and refetched as it
 * changes (CWA-173). A problem with it is shown but never blocks: going live
 * does not wait on its email, and a failed one is reported afterwards.
 */
const GoLiveDialog: React.FC<GoLiveDialogProps> = ({
  visible,
  prospectId,
  stations,
  confirmLabel,
  privateInEround = false,
  customerEmail,
  onHide,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { preview, loading, error, retry } = useEmailPreview(
    prospectId,
    "GO_LIVE",
    selected,
    // Nothing ticked means nothing to confirm, so nothing to preview either.
    visible && selected.length > 0
  );

  useEffect(() => {
    if (visible) {
      setSelected(stations.map((s) => s.station_id));
      setSubmitting(false);
    }
  }, [visible, stations]);

  const toggle = (stationId: string) =>
    setSelected((current) =>
      current.includes(stationId)
        ? current.filter((id) => id !== stationId)
        : [...current, stationId]
    );

  const submit = async () => {
    if (!selected.length) return;
    setSubmitting(true);
    try {
      await onConfirm(selected);
      onHide();
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label={confirmLabel ?? "Mark as live"}
        icon="pi pi-bolt"
        onClick={submit}
        loading={submitting}
        disabled={submitting || !selected.length}
      />
    </div>
  );

  return (
    <Dialog
      header="Mark stations as live"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "52rem", maxWidth: "95vw" }}
    >
      <p className="mt-0">
        Confirming this says you have already made this location public in
        eRound. The Back Office does not publish it — it records the stations as
        live and sends the confirmation email to{" "}
        <span className="font-medium">{customerEmail}</span>.
      </p>

      {/* Advisory, never blocking: an agent who has just published in eRound
          knows better than a flag read up to an hour ago. */}
      {privateInEround && (
        <Message
          severity="warn"
          className="w-full mb-3"
          text="eRound still has this location private. Publish it there first, or drivers will not see these stations."
        />
      )}

      <p className="text-500 text-sm">
        Choose which stations the email should mention. All of the customer's
        stations are recorded as live either way — the choice only changes what
        the email lists.
      </p>

      <div className="flex flex-column gap-2 mb-2">
        {stations.map((station) => (
          <div key={station.station_id} className="flex align-items-center gap-2">
            <Checkbox
              inputId={`go-live-${station.station_id}`}
              checked={selected.includes(station.station_id)}
              onChange={() => toggle(station.station_id)}
            />
            <label htmlFor={`go-live-${station.station_id}`} className="cursor-pointer">
              {/* One identity per station now, so printing both was printing
                  the same value twice (CWA-154 finding 4). */}
              {station.station_id}
            </label>
          </div>
        ))}
      </div>

      {!selected.length && (
        <Message
          severity="warn"
          className="w-full"
          text="Pick at least one station for the email to mention."
        />
      )}

      {!!selected.length && (
        <div className="mt-3">
          {preview && !preview.can_send && (
            <Message
              severity="error"
              className="w-full mb-2"
              text="The confirmation email will not be sent as it stands. The stations are still recorded as live if you confirm."
            />
          )}
          <EmailPreview
            preview={preview}
            loading={loading}
            errorMessage={
              error ? previewErrorMessage(error, goLiveErrorMessage) : null
            }
            onRetry={retry}
          />
        </div>
      )}
    </Dialog>
  );
};

export default GoLiveDialog;
